import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { MessageCategory } from "@/data/jobMessageTypes";
import { cn } from "@/lib/utils";
import {
  ShoppingCart,
  AlertCircle,
  MessageSquare,
} from "lucide-react";

interface JobChatTabsProps {
  value: MessageCategory;
  onChange: (value: MessageCategory) => void;
  unreadCounts?: Partial<Record<MessageCategory, number>>;
  categories?: MessageCategory[];
}

const TAB_CONFIG: Record<MessageCategory, { label: string; icon: React.ElementType; color: string }> = {
  orders: { label: "Orders", icon: ShoppingCart, color: "text-blue-500" },
  concerns: { label: "Concerns", icon: AlertCircle, color: "text-amber-500" },
  general: { label: "General", icon: MessageSquare, color: "text-slate-500" },
};

export function JobChatTabs({ value, onChange, unreadCounts = {}, categories = ["orders", "concerns", "general"] }: JobChatTabsProps) {
  const tabs: MessageCategory[] = categories;

  return (
    <Tabs value={value} onValueChange={onChange} className="w-full">
      <TabsList className={`grid bg-muted/30 h-11 rounded-lg p-1 ${categories.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
        {tabs.map((tab) => {
          const config = TAB_CONFIG[tab];
          const Icon = config.icon;
          const count = unreadCounts[tab] ?? 0;
          const active = value === tab;

          return (
            <TabsTrigger
              key={tab}
              value={tab}
              className={cn(
                "flex items-center justify-center gap-2 rounded-md transition-all text-sm font-medium",
                active
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Icon size={16} className={config.color} />
              {config.label}
              {count > 0 && (
                <Badge
                  variant={active ? "default" : "secondary"}
                  className="h-5 min-w-[20px] px-1 text-xs"
                >
                  {count}
                </Badge>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
